import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Translate } from "./types.js";

/** Copies text to the clipboard; falls back to a hidden textarea where the async API is missing. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path (insecure origins, denied permission).
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/** A button that copies `text` and says so. `label` overrides the default "Copy". */
export function CopyButton(props: {
  t: Translate;
  text: string;
  label?: string;
  className?: string;
}): ReactNode {
  const { t, text } = props;
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <button
      type="button"
      className={props.className ?? "btn btn-sm"}
      onClick={() => {
        void copyText(text).then((ok) => {
          if (!ok) return;
          setCopied(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      <span aria-live="polite">
        {copied ? t("ui.copy.copied") : (props.label ?? t("ui.copy.copy"))}
      </span>
    </button>
  );
}
